import { motion } from 'framer-motion';

export default function Skills() {
  const skillGroups = [
    {
      title: "Frontend Development",
      skills: ["React.js", "JavaScript (ES6+)", "HTML5", "CSS3", "Responsive Design"],
    },
    {
      title: "Backend Development",
      skills: ["Node.js", "Express.js", "RESTful APIs", "Authentication (JWT)", "API Integration"],
    },
    {
      title: "Database",
      skills: ["MongoDB", "Redis", "MySQL", "Database Design", "CRUD Operations"],
    },
    {
      title: "Systems Administration",
      skills: ["Linux Administration", "Windows Server", "User & Access Management", "System Monitoring", "Server Maintenance"],
    },
    {
      title: "Infrastructure & Networking",
      skills: ["VPS Management", "Domain & DNS Management", "Web Hosting", "cPanel", "SSL Configuration", "Basic Networking", "Firewall Configuration"],
    },
    {
      title: "DevOps & Deployment",
      skills: ["Docker", "Git", "GitHub", "CI/CD Fundamentals", "Nginx", "Application Deployment"],
    },
    {
      title: "Quality Assurance",
      skills: ["Manual Testing", "Regression Testing", "Functional Testing", "Cross-Browser Testing", "Test Case Design", "Bug Reporting", "UAT Support"],
    },
    {
      title: "Tools & Technologies",
      skills: ["Postman", "VS Code", "GitHub", "Docker Desktop", "Microsoft Office", "Remote Desktop"],
    },
  ];

  const stats = [
    { label: "Experience", value: "3+ Years" },
    { label: "Technologies", value: "35+" },
    { label: "Core Domains", value: "8 Domains" },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: "easeOut" } }
  };

  return (
    <section id="skills" className="py-24 px-6 bg-slate-50 text-slate-900 relative overflow-hidden">
      {/* Subtle Background Accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none opacity-40">
        <div className="absolute top-12 left-10 w-72 h-72 bg-indigo-100 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky-100 rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl mb-4"
          >
            Technical Expertise
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600 leading-relaxed mb-10"
          >
            Building reliable systems through Infrastructure, System Administration, Full-Stack Development, DevOps, and Quality Assurance.
          </motion.p>

          {/* Stats Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-flex flex-wrap justify-center items-center gap-8 px-8 py-4 bg-white/70 backdrop-blur-md border border-slate-200/80 rounded-2xl shadow-sm"
          >
            {stats.map((stat, i) => (
              <div key={i} className="flex items-center gap-3">
                {i > 0 && <span className="text-slate-300 hidden sm:block">•</span>}
                <div className="text-center sm:text-left">
                  <span className="block text-xl font-bold text-slate-900">{stat.value}</span>
                  <span className="block text-xs font-medium tracking-wider uppercase text-slate-500">{stat.label}</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Skills Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
        >
          {skillGroups.map((group, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -4, transition: { duration: 0.2 } }}
              className="bg-white/80 backdrop-blur-md border border-slate-200/60 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between"
            >
              <div>
                <h3 className="text-lg font-semibold text-slate-950 mb-4 tracking-tight">
                  {group.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {group.skills.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="bg-slate-100/80 text-slate-700 hover:bg-slate-900 hover:text-white px-3 py-1.5 rounded-xl text-xs font-medium border border-slate-200/40 transition-colors duration-200 cursor-default"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}